import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePlacesAutocomplete } from '@sweetgreen/garnish';

import { AddressSearchField } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import type { LocationResultsProps } from '../../../LocationScreen.types';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationAutocompleteInput = (
  props: LocationAutocompleteInputProps,
) => {
  const {
    searchString,
    shouldFocusInput,
    predictionsSearchString,
    loading: isLoadingLocations,
    setSearchString,
    setPredictionsData,
    setPredictionsSearchString,
    onChangeText,
    onResetDeliverySearch,
  } = props;

  const shouldDisableSearchControlsWhileLoading = useFeatureFlag(
    'XP-3189-short-disable-search-controls-while-fetching-locations',
  );
  const shouldDisableTextField =
    shouldDisableSearchControlsWhileLoading && isLoadingLocations;

  // ─────────────────────────────────────────────────────────────────

  const { current: initialSearchString } = useRef(searchString);

  const {
    setSearchString: setAutocompleteSearchString,
    predictions,
    loading,
    error,
    noResults,
  } = usePlacesAutocomplete({ apiKey: getEnvVars().GOOGLE_API_KEY });

  // ─────────────────────────────────────────────────────────────────

  const predictionData = useMemo(() => {
    return { predictions, loading, error, noResults };
  }, [error, loading, noResults, predictions]);

  // ─── Helpers ─────────────────────────────────────────────────────

  const handleSearchStringChange = useCallback(
    (currentSearchString: string) => {
      const shouldResetCurrentDeliverySearchState =
        currentSearchString.trim().length === 0;

      setAutocompleteSearchString(currentSearchString);
      setPredictionsSearchString?.(currentSearchString);
      onChangeText?.(currentSearchString);

      // reset delivery search state after clearing the predictions search string
      if (shouldResetCurrentDeliverySearchState) onResetDeliverySearch(true);
    },
    [
      onChangeText,
      onResetDeliverySearch,
      setAutocompleteSearchString,
      setPredictionsSearchString,
    ],
  );

  // ─── Effects ─────────────────────────────────────────────────────

  // Fetch predictions on mount
  useEffect(() => {
    if (!initialSearchString) return;

    setAutocompleteSearchString(initialSearchString);
  }, [initialSearchString, setAutocompleteSearchString]);

  // Sync predictions states
  useEffect(() => {
    setPredictionsData(predictionData);
  }, [predictionData, setPredictionsData]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <AddressSearchField
      value={predictionsSearchString}
      shouldFocusInput={shouldFocusInput}
      hasResults={!noResults}
      onChangeText={handleSearchStringChange}
      onSubmit={setSearchString}
      disabled={shouldDisableTextField}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

type LocationAutocompleteInputProps = {
  readonly shouldFocusInput?: boolean;
  readonly setSearchString?: (searchString: string) => void;
  readonly onChangeText?: (searchString: string) => void;
  readonly setPredictionsSearchString?: LocationResultsProps['setPredictionsSearchString'];
} & Pick<
  LocationResultsProps,
  | 'setPredictionsData'
  | 'searchString'
  | 'onResetDeliverySearch'
  | 'predictionsSearchString'
  | 'loading'
>;
